// Affichage des erreurs d'envoi e-factura, bilingue EN + RO.
//
// Avant, le store faisait `alert("POST ISSUE")` sur n'importe quel rejet axios :
// adresse de facturation manquante, certificat ANAF non reconnu, 5xx de l'ANAF,
// timeout, coupure réseau — tout donnait le même message opaque, et le vrai
// message de l'API finissait dans la console. Pire, sur plusieurs écrans le
// `$vs.notify` vert « eFactura Sent to SPV! » était écrit HORS de la promesse et
// s'affichait donc même quand l'envoi avait été refusé.
//
// L'API renvoie désormais { code, message, message_ro, details } (voir
// EFACTURA_ERRORS dans api/app/controllers/spv.controller.js). Ce mixin les
// affiche : titre en anglais (langue de l'UI), texte en roumain (langue des
// échanges avec l'ANAF et le comptable).

// Codes traités comme « problème de données à corriger chez nous » plutôt que
// comme un incident ANAF — utile pour orienter l'opérateur vers l'écran ÉDITER.
const DATA_ERROR_CODES = ["MISSING_BILLING_ADDRESS", "MISSING_BUYER_CUI", "MISSING_OPERATION"]

// Conclusion posée par le robot « messages ANAF » (operations.efactura_alert,
// migration 103, api/app/services/efactura/anafmessages.js). Règle de Chady
// (2026-10-29) : la fiche seule décide — un CUI, un numéro de TVA ou un J = une
// société ; rien (ou un CNP) = une personne physique. Jamais le commutateur
// COMPANY/PERSON (la borne met tout le monde en COMPANY), jamais le nom.
//   NOT_DELIVERED  BT-47 et la fiche porte un identifiant de société aujourd'hui :
//                  partie en zéros à tort → storno + réémission (rouge).
//   B2C_PERSON     BT-47 et rien sur la fiche : B2C normal, rien à faire (gris) ;
//                  un CUI ajouté plus tard → rouge au passage suivant.
//   ANAF_ERROR     XML refusé.
const EFACTURA_ALERTS = {
  NOT_DELIVERED: {
    color: 'danger',
    title: 'ANAF validated this e-Factura but did NOT deliver it to the customer',
    text: 'The invoice left without a customer identifier (BT-47) and the ANAF reported it as B2C, while the customer card carries a company identifier today. It is signed and indexed, but it never reached the customer\'s SPV. Issue a storno and re-issue the invoice: it now leaves with the CUI.'
  },
  ANAF_ERROR: {
    color: 'danger',
    title: 'ANAF refused this e-Factura',
    text: 'The ANAF reported errors on the XML. Fix the cause and send the invoice again.'
  },
  B2C_PERSON: {
    color: 'dark',
    title: 'ANAF reported this e-Factura as B2C (private person)',
    text: 'The customer card has no CUI, VAT number or J number, so this is a private person: a B2C invoice is normal and nothing is due. If this customer is in fact a company, add its CUI on the card: the next hourly check turns this into a red alert (storno and re-issue).'
  }
}

export default {
  computed: {
    /** { code, color, title, text, detail, at } ou null — lu sur `user_data` (la facture affichée). */
    efacturaAlert () {
      const d = this.user_data
      if (!d || !d.efactura_alert) return null
      const code = d.efactura_alert
      const t = EFACTURA_ALERTS[code] || { color: 'danger', title: `ANAF reported: ${code}`, text: '' }
      let at = ''
      if (d.efactura_alert_at) {
        const parsed = new Date(d.efactura_alert_at)
        if (!isNaN(parsed.getTime())) {
          const p = (n) => String(n).padStart(2, '0')
          at = `${p(parsed.getDate())}/${p(parsed.getMonth() + 1)}/${parsed.getFullYear()} ${p(parsed.getHours())}:${p(parsed.getMinutes())}`
        }
      }
      return { code, color: t.color, title: t.title, text: t.text, detail: d.efactura_alert_detail || '', at }
    },
  },
  methods: {
    /** Message lisible (EN) extrait d'une erreur axios. */
    efacturaErrorMessage (err) {
      const data = err && err.response && err.response.data
      if (data && data.message) return data.message
      if (typeof data === 'string' && data.trim()) return data.trim().slice(0, 500)
      return (err && err.message) || 'Unknown error while sending to ANAF.'
    },

    /** Message roumain, quand l'API en fournit un. */
    efacturaErrorMessageRo (err) {
      const data = err && err.response && err.response.data
      return (data && data.message_ro) || ''
    },

    efacturaErrorCode (err) {
      const data = err && err.response && err.response.data
      return (data && data.code) || null
    },

    /** Notification rouge détaillée. À appeler dans TOUS les .catch d'envoi e-factura. */
    efacturaErrorNotify (err) {
      const code = this.efacturaErrorCode(err)
      const en = this.efacturaErrorMessage(err)
      const ro = this.efacturaErrorMessageRo(err)
      const data = err && err.response && err.response.data
      const details = (data && data.details) || ''

      const isDataError = DATA_ERROR_CODES.indexOf(code) >= 0
      const title = isDataError ? 'eFactura NOT sent — fix the invoice' : 'eFactura NOT sent — ANAF'

      const text = [en, ro, details].filter(Boolean).join('\n')
      this.$vs.notify({
        color: 'danger',
        title,
        text,
        time: 12000,
      })
      console.error('eFactura error', code || '(no code)', err)
    },
  },
}
